<!-- src/views/TrainingView.vue -->
<template>
  <div class="container box_component">
    <div class="header-title">
      <h2>{{ $t('training.title') }}</h2>
    </div>

    <div class="role-switch" v-if="showSwitcher">
      <button
        v-for="mode in availableModes"
        :key="mode.key"
        class="switch-btn"
        :class="{ active: activeMode === mode.key }"
        @click="activeMode = mode.key"
      >
        {{ mode.label }}
      </button>
    </div>

    <AthleteTrainingPanel
      v-if="activeMode === 'athlete' && canShowAthlete"
      :mode-label="activeModeLabel"
    />
    <JudgeTrainingPanel
      v-else-if="activeMode === 'judge' && canShowJudge"
      :mode-label="activeModeLabel"
    />
    <SupervisorTrainingPanel
      v-else-if="activeMode === 'supervisor' && canShowSupervisor"
      :mode-label="activeModeLabel"
    />

    <div v-else class="text-center text-muted mt-3">
      {{ $t('training.no_access') || 'No tienes acceso a entrenamiento' }}
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { isAthlete, isJudge, isSupervisor } from '@/helpers/roleUtils';
import AthleteTrainingPanel from '@/components/training/AthleteTrainingPanel.vue';
import JudgeTrainingPanel from '@/components/training/JudgeTrainingPanel.vue';
import SupervisorTrainingPanel from '@/components/training/SupervisorTrainingPanel.vue';

export default {
  name: 'TrainingView',
  components: { AthleteTrainingPanel, JudgeTrainingPanel, SupervisorTrainingPanel },
  data() {
    return {
      activeMode: null,
    };
  },
  computed: {
    ...mapState('user', ['user']),
    ...mapState('competition', ['appNavigation']),
    canShowAthlete() {
      return isAthlete(this.user);
    },
    canShowJudge() {
      return isJudge(this.user);
    },
    canShowSupervisor() {
      return isSupervisor(this.user);
    },
    availableModes() {
      const modes = [];
      if (this.canShowAthlete) {
        modes.push({ key: 'athlete', label: (this.$t('training.tabs.athlete') || 'Atleta') });
      }
      if (this.canShowJudge) {
        modes.push({ key: 'judge', label: (this.$t('training.tabs.judge') || 'Juez') });
      }
      if (this.canShowSupervisor) {
        modes.push({ key: 'supervisor', label: (this.$t('training.tabs.supervisor') || 'Supervisor') });
      }
      return modes;
    },
    showSwitcher() {
      return this.availableModes.length > 1;
    },
    activeModeLabel() {
      const current = this.availableModes.find(mode => mode.key === this.activeMode);
      return current?.label || '';
    }
  },
  methods: {
    ...mapActions('competition', ['updateAppNavigationZone']),
    persistActiveMode(mode) {
      if (!mode) return;
      this.updateAppNavigationZone({
        zone: 'training',
        data: {
          lastMode: mode,
        },
      });
    },
  },
  watch: {
    activeMode(newMode) {
      this.persistActiveMode(newMode);
    },
    availableModes: {
      immediate: true,
      handler(modes) {
        if (!modes.length) {
          this.activeMode = null;
          return;
        }

        const savedMode = this.appNavigation?.training?.lastMode || null;
        if (savedMode && modes.some(mode => mode.key === savedMode)) {
          this.activeMode = savedMode;
          return;
        }

        if (!modes.some(mode => mode.key === this.activeMode)) {
          this.activeMode = modes[0].key;
        }
      }
    }
  },
};
</script>

<style scoped>
.role-switch {
  display: inline-flex;
  gap: 8px;
  padding: 6px;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  background: #f8fafc;
  margin: 10px 0 4px;
}

.switch-btn {
  border: none;
  background: transparent;
  padding: 8px 14px;
  border-radius: 999px;
  font-weight: 600;
  color: #4b5563;
  cursor: pointer;
}

.switch-btn.active {
  background: #e0e7ff;
  color: #1d4ed8;
}

.switch-btn:not(.active):hover {
  background: #f1f5f9;
}

.header-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
</style>
